import { useState, useEffect } from 'react';
import { OnboardingState } from '../OnboardingWizard';
import { getGDAPConsentUrl, assignExchangeRole } from '../../../services/api';
import {
  ShieldCheck,
  CheckCircle,
  RefreshCw,
  ExternalLink,
  Copy,
  ClipboardCheck,
} from 'lucide-react';

interface Props {
  state: OnboardingState;
  updateState: (updates: Partial<OnboardingState>) => void;
  onNext: () => void;
  setError: (error: string | null) => void;
}

export default function AppConsentStep({ state, updateState, onNext, setError }: Props) {
  const [loading, setLoading] = useState(false);
  const [consentUrl, setConsentUrl] = useState<string | null>(null);
  const [consentGranted, setConsentGranted] = useState(state.appConsentGranted);
  const [exchangeAssigned, setExchangeAssigned] = useState(state.exchangeRoleAssigned);
  const [assigningExchange, setAssigningExchange] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Fetch consent URL on mount if we have a tenant ID
    if (state.customerTenantId && !consentUrl) {
      fetchConsentUrl();
    }
  }, [state.customerTenantId]);

  const fetchConsentUrl = async () => {
    if (!state.customerTenantId) {
      setError('No customer tenant ID available');
      return;
    }

    setLoading(true);
    try {
      const result = await getGDAPConsentUrl(state.customerTenantId);
      if (result.error) {
        setError(result.error);
      } else {
        setConsentUrl(result.consentUrl);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get consent URL');
    } finally {
      setLoading(false);
    }
  };

  const openConsentPopup = () => {
    if (!consentUrl) return;

    window.open(
      consentUrl,
      'AppConsent',
      'width=800,height=700,menubar=no,toolbar=no,scrollbars=yes'
    );

    // Note: We can't actually detect consent completion automatically
    // The user will need to manually confirm
  };

  const copyToClipboard = async () => {
    if (!consentUrl) return;

    try {
      await navigator.clipboard.writeText(consentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleConsentConfirmed = () => {
    setConsentGranted(true);
    updateState({ appConsentGranted: true });
  };

  const handleAssignExchangeRole = async () => {
    if (!state.customerTenantId) {
      setError('No customer tenant ID available');
      return;
    }

    setAssigningExchange(true);
    setError(null);

    try {
      const result = await assignExchangeRole(state.customerTenantId);
      if (result.error) {
        setError(result.error);
      } else {
        setExchangeAssigned(true);
        updateState({ exchangeRoleAssigned: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign Exchange role');
    } finally {
      setAssigningExchange(false);
    }
  };

  const allComplete = consentGranted;

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <ShieldCheck className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Application Consent
            </h2>
            <p className="text-sm text-muted-foreground">
              Grant the Reports application access to the customer tenant
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* App Consent Section */}
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <div className={`p-2 rounded-lg mr-3 ${consentGranted ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
                {consentGranted ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <ShieldCheck className="h-5 w-5 text-blue-600" />
                )}
              </div>
              <div>
                <h4 className="font-medium text-foreground">
                  Reports Application Consent
                </h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Required for security reports, MFA status, and user management
                </p>
              </div>
            </div>
            {consentGranted && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
                Granted
              </span>
            )}
          </div>

          {!consentGranted && (
            <div className="mt-4 space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-3">
                <h5 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">
                  The customer's Global Admin must consent to these permissions:
                </h5>
                <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-1">
                  <li>• User.Read.All / User.ReadWrite.All - Read and manage users</li>
                  <li>• GroupMember.ReadWrite.All - Add/remove users from groups</li>
                  <li>• Directory.Read.All - Read directory data</li>
                  <li>• AuditLog.Read.All - Read audit logs</li>
                  <li>• Reports.Read.All - Read usage reports</li>
                  <li>• UserAuthenticationMethod.ReadWrite.All - Manage MFA</li>
                </ul>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground mr-2" />
                  <span className="text-muted-foreground">Loading consent URL...</span>
                </div>
              ) : consentUrl ? (
                <>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      readOnly
                      value={consentUrl}
                      className="input flex-1 text-xs font-mono"
                    />
                    <button
                      onClick={copyToClipboard}
                      className="btn-secondary px-3"
                      title="Copy URL"
                    >
                      {copied ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <Copy className="h-5 w-5" />
                      )}
                    </button>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={openConsentPopup}
                      className="btn-primary flex-1"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open Consent Window
                    </button>
                    <button
                      onClick={handleConsentConfirmed}
                      className="btn-secondary"
                    >
                      <ClipboardCheck className="h-4 w-4 mr-2" />
                      I've Completed Consent
                    </button>
                  </div>
                </>
              ) : (
                <button
                  onClick={fetchConsentUrl}
                  className="btn-secondary w-full"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Get Consent URL
                </button>
              )}
            </div>
          )}
        </div>

        {/* Exchange Role Assignment (Optional - shows after consent) */}
        {consentGranted && (
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <div className={`p-2 rounded-lg mr-3 ${exchangeAssigned ? 'bg-green-100 dark:bg-green-900/30' : 'bg-muted'}`}>
                  {exchangeAssigned ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <ShieldCheck className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <h4 className="font-medium text-foreground">
                    Exchange Administrator Role (Optional)
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Assign Exchange admin role to the Reports app service principal
                  </p>
                </div>
              </div>
              {exchangeAssigned && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
                  Assigned
                </span>
              )}
            </div>

            {!exchangeAssigned && (
              <div className="mt-4">
                <button
                  onClick={handleAssignExchangeRole}
                  disabled={assigningExchange}
                  className="btn-secondary"
                >
                  {assigningExchange ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Assigning...
                    </>
                  ) : (
                    'Assign Exchange Role'
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Continue Button */}
        {allComplete && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
              <div>
                <h4 className="font-medium text-green-800 dark:text-green-200">
                  Setup Complete!
                </h4>
                <p className="text-sm text-green-700 dark:text-green-300">
                  All required permissions have been configured. The client is ready to use.
                </p>
              </div>
            </div>
          </div>
        )}

        {allComplete && (
          <div className="pt-4 border-t border-border">
            <button onClick={onNext} className="btn-primary w-full sm:w-auto">
              Complete Onboarding &rarr;
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
